import { classMgmtService } from "./classMgmtService";
import type {
  ClassListItem,
  SessionParticipant,
  SessionWithDetails,
} from "../../types/domain";

const FEED_PAGE_SIZE = 200;

export type ParticipantRole = "teacher" | "trainer" | "student";

export type ParticipantStatus =
  | "unmarked"
  | "checked_in"
  | "checked_out"
  | "absent"
  | "excused_absence"
  | "other";

export interface ParticipantSessionRow {
  sessionId: string;
  sessionTitle: string;
  sessionStatus: SessionWithDetails["status"];
  classId: string | null;
  className: string;
  startTime: string | null;
  endTime: string | null;
  userId: string;
  userCode?: string | null;
  userName: string;
  userEmail: string;
  role: ParticipantRole;
  attendanceId: string | null;
  rawStatus: string | null;
  status: ParticipantStatus;
  isCheckedIn: boolean;
  checkedInAt: string | null;
  checkedOutAt: string | null;
}

type Translate = (key: string) => string;

interface GetParticipantRowsParams {
  fromDate?: string;
  toDate?: string;
  classId?: string;
  roles?: ParticipantRole[];
  /** Translates fallback labels; without it the raw keys are returned. */
  t?: Translate;
}

const identity: Translate = (key) => key;

const normalizeStatus = (status?: string | null): ParticipantStatus => {
  if (!status) return "unmarked";
  const normalized = status.toLowerCase();
  if (normalized === "checked_in") return "checked_in";
  if (normalized === "checked_out") return "checked_out";
  if (normalized === "absent") return "absent";
  if (normalized === "excused_absence") return "excused_absence";
  return "other";
};

const isCheckedInStatus = (status: ParticipantStatus) =>
  status === "checked_in" || status === "checked_out";

const toRangeStart = (date?: string) => (date ? `${date}T00:00:00` : undefined);
const toRangeEnd = (date?: string) => (date ? `${date}T23:59:59` : undefined);

const participantName = (participant: SessionParticipant, t: Translate) =>
  participant.user?.full_name ||
  participant.user?.email ||
  t("participantInsights.unknownUser");

const participantEmail = (participant: SessionParticipant) =>
  participant.user?.email || "";

const flattenParticipants = (
  sessions: SessionWithDetails[],
  roles: Set<ParticipantRole>,
  t: Translate
): ParticipantSessionRow[] => {
  const rows: ParticipantSessionRow[] = [];

  sessions.forEach((session) => {
    const participants = Array.isArray(session.participants)
      ? session.participants
      : [];

    participants.forEach((participant) => {
      if (!roles.has(participant.role)) return;

      const status = normalizeStatus(participant.status);

      rows.push({
        sessionId: session.id,
        sessionTitle: session.title || t("participantInsights.session"),
        sessionStatus: session.status,
        classId: session.class_id ?? null,
        className: session.class_name ?? t("participantInsights.unknownClass"),
        startTime: session.start_time ?? null,
        endTime: session.end_time ?? null,
        userId: participant.user_id,
        userCode: participant.user?.code ?? null,
        userName: participantName(participant, t),
        userEmail: participantEmail(participant),
        role: participant.role,
        attendanceId: participant.attendance_id ?? null,
        rawStatus: participant.status ?? null,
        status,
        isCheckedIn: isCheckedInStatus(status),
        checkedInAt: participant.checked_in_at ?? null,
        checkedOutAt: participant.checked_out_at ?? null,
      });
    });
  });

  return rows.sort((left, right) => {
    const leftValue = left.startTime ? new Date(left.startTime).getTime() : 0;
    const rightValue = right.startTime
      ? new Date(right.startTime).getTime()
      : 0;
    return rightValue - leftValue;
  });
};

const getAllSessionsFeed = async (params: {
  fromDate?: string;
  toDate?: string;
  classId?: string;
}): Promise<SessionWithDetails[]> => {
  let skip = 0;
  const items: SessionWithDetails[] = [];

  while (true) {
    const response = await classMgmtService.getSessionsFeed({
      date_from: toRangeStart(params.fromDate),
      date_to: toRangeEnd(params.toDate),
      class_id: params.classId,
      skip,
      limit: FEED_PAGE_SIZE,
    });

    items.push(...response.items);

    if (response.items.length < FEED_PAGE_SIZE) {
      break;
    }

    skip += FEED_PAGE_SIZE;
    if (skip >= response.total) {
      break;
    }
  }

  return items;
};

const getAllStaffSessionsFeed = async (params: {
  fromDate?: string;
  toDate?: string;
  centerId?: string;
}): Promise<SessionWithDetails[]> => {
  const response = await classMgmtService.getStaffTimesheetSessions({
    date_from: toRangeStart(params.fromDate) ?? "",
    date_to: toRangeEnd(params.toDate) ?? "",
    center_id: params.centerId,
  });
  return response.items;
};

const getClasses = async (params?: {
  centerId?: string;
}): Promise<ClassListItem[]> => {
  // When a centerId is provided, scope the class list to that center so callers
  // (e.g. System Admin acting-as-center-owner) only see classes in the selected
  // center instead of the global list.
  const response = await classMgmtService.getAll({
    skip: 0,
    limit: 200,
    center_id: params?.centerId,
  });
  return response.items;
};

const getParticipantRows = async (
  params: GetParticipantRowsParams & { centerId?: string }
): Promise<ParticipantSessionRow[]> => {
  const t = params.t ?? identity;
  const roles = new Set<ParticipantRole>(
    params.roles ?? ["teacher", "trainer", "student"]
  );
  const staffOnly =
    roles.size > 0 && Array.from(roles).every((role) => role !== "student");

  // Staff-only queries (e.g. the timesheet screen) use a dedicated endpoint
  // that filters participants to teacher/trainer AND center at the SQL
  // level, so no client-side /classes allowlist call is needed to scope by
  // center — the backend does it directly via center_id.
  if (staffOnly && !params.classId) {
    const sessions = await getAllStaffSessionsFeed({
      fromDate: params.fromDate,
      toDate: params.toDate,
      centerId: params.centerId,
    });
    return flattenParticipants(sessions, roles, t);
  }

  // Fallback path (class-scoped or mixed-role queries): /sessions/feed has
  // no center_id param, so scope client-side using the center's class IDs.
  let scopedClassId = params.classId;
  let centerClasses: ClassListItem[] | null = null;
  if (params.centerId) {
    centerClasses = await getClasses({ centerId: params.centerId });
    const classIds = new Set(centerClasses.map((item) => item.id));
    if (params.classId && params.classId !== "all") {
      scopedClassId = classIds.has(params.classId) ? params.classId : undefined;
    } else {
      scopedClassId = undefined;
    }
    if (!scopedClassId && classIds.size === 0) {
      return [];
    }
  }

  const sessions = await getAllSessionsFeed({
    fromDate: params.fromDate,
    toDate: params.toDate,
    classId: scopedClassId,
  });

  const allowedClassIds = centerClasses
    ? new Set(centerClasses.map((item) => item.id))
    : null;

  const filteredSessions = allowedClassIds
    ? sessions.filter(
        (session) => session.class_id && allowedClassIds.has(session.class_id)
      )
    : sessions;

  return flattenParticipants(filteredSessions, roles, t);
};

export const participantInsightsService = {
  getClasses,
  getParticipantRows,
};
